<template>
  <div class="help-center__component">
    <HelpCenterProgress
      class="help-center__progress"
      :active="fetchInProgress"
    />
    <div class="help-center__mobile-overhang">
      <HelpCenterMobileHero
        :menu="frame.menus.mobile_menu"
        :selected-tab="selectedTab"
        :external-links="frame.external_links.mobile_external_links"
        :search-suggestions="frame.search_suggestions"
      />
      <HelpCenterMobileNav
        :type="frame.type"
        :categories="frame.categories"
        :selected-tab="selectedTab"
        :selected-category="selectedCategory"
      />
    </div>
    <HelpCenterDesktopHero
      class="help-center__desktop-hero"
      :tabs="frame.tabs"
      :admin-url="adminUrl"
      :selected-tab="selectedTab"
      :external-tabs="frame.external_tabs"
      :external-links="frame.external_links.desktop_external_links"
      :search-suggestions="frame.search_suggestions"
    />
    <div
      class="help-center__columns"
      :class="columnsClass"
    >
      <HelpCenterFilters
        class="help-center__filters"
        :type="frame.type"
        :categories="frame.categories"
        :selected-tab="selectedTab"
        :selected-category="selectedCategory"
      />
      <div
        class="help-center__main"
        :class="contentClass"
      >
        <HelpCenterBreadcrumbs
          v-if="breadcrumbs"
          :post="frame.post"
          :tab-map="tabMap"
          :category-map="categoryMap"
          :breadcrumbs="breadcrumbs"
        />
        <HelpCenterPost
          v-if="frame.post"
          :post="frame.post"
          :author-map="authorMap"
          :category-map="categoryMap"
        />
        <HelpCenterPostFlow
          v-else-if="frame.paginated_posts"
          :author-map="authorMap"
          :category-map="categoryMap"
          :paginated-posts="frame.paginated_posts"
        />
        <HelpCenterPostTree
          v-else-if="showsPostTree"
          :post-map="postMap"
          :categories="postTreeCategories"
        />
        <HelpCenterPostWall
          v-else-if="showsPostWall"
          :post-map="postMap"
          :author-map="authorMap"
          :default-posts="frame.default_posts"
          :selected-tab="selectedTab"
          :selected-category="selectedCategory"
        />
      </div>
      <HelpCenterPostAside
        v-if="frame.post"
        :type="frame.type"
        :post="frame.post"
        :author-map="authorMap"
        :category-map="categoryMap"
        :root-fragment="rootFragment"
        class="help-center__aside"
      />
    </div>
    <HelpCenterFooter
      class="help-center__footer"
      :menu="frame.menus.desktop_menu"
    />
    <HelpCenterCatchAll />
    <HelpCenterError main :error="error" />
  </div>
</template>

<script setup>
import { toRef, watch, computed } from 'vue'
import { keyBy } from 'gleam-shared/helpers/nodash'
import flattenTree from 'gleam-shared/helpers/flattenTree'

import HelpCenterError from 'help-center/components/HelpCenterError'
import HelpCenterFooter from 'help-center/components/HelpCenterFooter'
import HelpCenterFilters from 'help-center/components/HelpCenterFilters'
import HelpCenterCatchAll from 'help-center/components/HelpCenterCatchAll'
import HelpCenterProgress from 'help-center/components/HelpCenterProgress'
import HelpCenterPostAside from 'help-center/components/HelpCenterPostAside'
import HelpCenterMobileNav from 'help-center/components/HelpCenterMobileNav'
import HelpCenterMobileHero from 'help-center/components/HelpCenterMobileHero'
import HelpCenterDesktopHero from 'help-center/components/HelpCenterDesktopHero'
import HelpCenterBreadcrumbs from 'help-center/components/HelpCenterBreadcrumbs'

import HelpCenterPost from 'help-center/components/HelpCenterPost'
import HelpCenterPostFlow from 'help-center/components/HelpCenterPostFlow'
import HelpCenterPostTree from 'help-center/components/HelpCenterPostTree'
import HelpCenterPostWall from 'help-center/components/HelpCenterPostWall'

import useHelpCenterFrameManager from 'help-center/composables/useHelpCenterFrameManager'
import useHelpCenterHistoryManager from 'help-center/composables/useHelpCenterHistoryManager'
import useHelpCenterFragmentTracker from 'help-center/composables/useHelpCenterFragmentTracker'
import useHelpCenterNavigationInterrupter from 'help-center/composables/useHelpCenterNavigationInterrupter'

import HELP_CENTER_TYPES from 'help-center/constants/types'

const props = defineProps({
  sourceFrame: {
    type: Object,
    required: true
  }
})

const sourceFrame = toRef(props, 'sourceFrame')
const requestedRoute = useHelpCenterNavigationInterrupter(sourceFrame)
const [error, frame, currentRoute, fetchInProgress] = useHelpCenterFrameManager(sourceFrame, requestedRoute)
const rootFragment = useHelpCenterFragmentTracker(toRef(frame, 'post'))
useHelpCenterHistoryManager(frame, currentRoute)

const columnsClass = computed(() => {
  if (frame.post) {
    return 'help-center__columns--renders-post'
  } else {
    return null
  }
})

const contentClass = computed(() => {
  if (frame.post) {
    return 'help-center__main--renders-post'
  } else if (frame.paginated_posts) {
    return 'help-center__main--renders-flow'
  } else {
    return null
  }
})

const flatCategories = computed(() => {
  return flattenTree(frame.categories, 'categories')
})

const tabMap = computed(() => {
  return keyBy(frame.tabs, 'url')
})

const postMap = computed(() => {
  return keyBy(frame.posts, 'url')
})

const authorMap = computed(() => {
  return keyBy(frame.authors, 'id')
})

const categoryMap = computed(() => {
  return keyBy(flatCategories.value, 'url')
})

const breadcrumbs = computed(() => {
  return frame.post?.breadcrumbs ?? selectedCategory.value?.breadcrumbs
})

const selectedTab = computed(() => {
  return tabMap.value[frame.selected_tab]
})

const selectedCategory = computed(() => {
  const url = frame.post?.selected_category ?? frame.selected_category
  return categoryMap.value[url]
})

const postTreeCategories = computed(() => {
  if (selectedCategory.value) {
    return [selectedCategory.value]
  } else {
    return frame.categories
  }
})

const adminUrl = computed(() => {
  if (frame.post) {
    return frame.post.admin_url
  } else if (selectedCategory.value) {
    return selectedCategory.value.admin_url
  } else if (selectedTab.value) {
    return selectedTab.value.admin_url
  } else {
    return null
  }
})

const metaTitle = computed(() => {
  if (frame.post) {
    return frame.post.meta_title
  } else if (selectedCategory.value) {
    return selectedCategory.value.meta_title
  } else if (selectedTab.value) {
    return selectedTab.value.meta_title
  } else {
    return null
  }
})

const showsPostTree = computed(() => {
  return frame.type === HELP_CENTER_TYPES.DOCS || frame.type === HELP_CENTER_TYPES.FAQS
})

const showsPostWall = computed(() => {
  return frame.type === HELP_CENTER_TYPES.GUIDES || frame.type === HELP_CENTER_TYPES.MODERN_BLOG
})

watch(metaTitle, (value) => {
  document.title = value
})
</script>

<style lang="scss">
@import 'help-center/mixins';

$filtersWidth: 260px;

html {
  background-color: var(--help-center-hero-bg);
  scroll-padding-top: calc(#{$helpCenterMobileHeroHeight} + #{$helpCenterMobileNavHeight});

  @include help-center-breakpoint(md) {
    scroll-padding-top: $helpCenterDesktopHeroHeight;
  }
}

body {
  min-width: var(--help-center-body-min-width);
  background-color: white;
}

.help-center {
  &__component {
    min-height: 100vh;
  }

  &__progress {
    position: fixed;
    top: 0;
    left: 0;
    z-index: 4;
  }

  &__mobile-overhang {
    @include help-center-hide-when(md);
    top: 0;
    z-index: 3;
    position: sticky;
  }

  &__desktop-hero {
    @include help-center-show-when(md);
    top: 0;
    z-index: 3;
    position: sticky;
  }

  &__columns {
    display: block;

    @include help-center-breakpoint(md) {
      display: grid;
      grid-template-columns: $filtersWidth calc(100% - #{$filtersWidth});
    }

    @include help-center-breakpoint(lg) {
      &--renders-post {
        grid-template-columns: $filtersWidth calc(100% - #{$filtersWidth} - #{$helpCenterAsideWidth}) $helpCenterAsideWidth;
      }
    }
  }

  &__filters {
    @include help-center-show-when(md);
    top: $helpCenterDesktopHeroHeight;
    height: calc(100vh - #{$helpCenterDesktopHeroHeight});
    position: sticky;
  }

  &__main {
    gap: 20px;
    display: flex;
    flex-direction: column;

    width: 100%;
    margin: 0 auto;
    padding: $helpCenterMobileVerPadding $helpCenterMobileHorPadding;
    max-width: 768px;

    @include help-center-breakpoint(md) {
      gap: 24px;
      padding: $helpCenterContentPadding;
      max-width: 1200px;

      &--renders-post {
        max-width: calc(var(--help-center-post-max-width) + #{$helpCenterContentPadding*2})
      }

      &--renders-flow {
        max-width: calc(var(--help-center-post-max-width) + #{$helpCenterContentPadding*3} + #{$helpCenterPostPillsWidth});
      }
    }
  }

  &__aside {
    @include help-center-show-when(lg);
    top: $helpCenterDesktopHeroHeight;
    height: calc(100vh - #{$helpCenterDesktopHeroHeight});
    padding: $helpCenterContentPadding 20px $helpCenterContentPadding 0;
    position: sticky;
  }

  &__footer {
    @include help-center-show-when(md);
  }
}
</style>
